import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { Plus, Edit2, Trash2, Power, Eye, Upload, FileText, Link as LinkIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';
import { storageService } from '@/services/storageService';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from '@/lib/utils';
import { Save, Play, FileUp, Youtube, CheckCircle2, Loader2, Video, ChevronRight, ChevronLeft } from 'lucide-react';

interface AdminCourse {
    id: string;
    title: string;
    slug: string;
    description: string;
    thumbnail: string;
    status: 'active' | 'draft';
    created_at: string;
}

interface Lesson {
    id: string;
    course_id: string;
    module_id?: string;
    title: string;
    video_url: string;
    stream_video_id?: string;
    description: string;
    duration: string;
    order_index: number;
}

interface LessonAsset {
    id: string;
    lesson_id: string;
    title: string;
    file_url: string;
    type: 'pdf' | 'link';
}

interface Module {
    id: string;
    course_id: string;
    title: string;
    order_index: number;
}

interface Plan {
    id?: string;
    course_id?: string;
    name: string;
    price: number;
    original_price?: number;
    duration: string;
    features: string[];
    popular: boolean;
    plan_key: string;
}

const AdminCourses = () => {
    const navigate = useNavigate();
    const [courses, setCourses] = useState<AdminCourse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditingCourse, setIsEditingCourse] = useState(false);
    const [currentCourse, setCurrentCourse] = useState<Partial<AdminCourse>>({});
    const PLAN_DEFAULTS = {
        STARTER: ['1 Month Access', 'Basic SMC Introduction', 'Chart Mastery Basics', 'Live Support'],
        PROFESSIONAL: ['3 Months Access', 'Advanced SMC Strategies', 'Weekly Q&A Sessions', 'Premium Discord Access'],
        PREMIUM: ['6 Months Access', 'Personalized Mentorship', 'The Private Strategy', 'Life-Time Community Sync']
    };

    const getDefaults = (name: string) => {
        const lowerName = name.toLowerCase();
        if (lowerName.includes('starter')) return PLAN_DEFAULTS.STARTER;
        if (lowerName.includes('professional')) return PLAN_DEFAULTS.PROFESSIONAL;
        if (lowerName.includes('premium')) return PLAN_DEFAULTS.PREMIUM;
        return [];
    };

    const [coursePlans, setCoursePlans] = useState<Plan[]>([
        { name: 'STARTER', price: 4999, original_price: 9999, duration: '1 MONTH ACCESS', features: PLAN_DEFAULTS.STARTER, popular: false, plan_key: 'starter' },
        { name: 'PROFESSIONAL', price: 7999, original_price: 15999, duration: '3 MONTHS ACCESS', features: PLAN_DEFAULTS.PROFESSIONAL, popular: true, plan_key: 'professional' },
        { name: 'PREMIUM', price: 9999, original_price: 29999, duration: '6 MONTHS ACCESS', features: PLAN_DEFAULTS.PREMIUM, popular: false, plan_key: 'premium' }
    ]);
    const [isUploading, setIsUploading] = useState(false);

    const loadCourses = async () => {
        setIsLoading(true);
        const data = await safeFetch<AdminCourse[]>(
            supabase.from('courses').select('*').order('created_at', { ascending: false }),
            [],
            'Loading courses'
        );
        setCourses(data);
        setIsLoading(false);
    };

    const loadPlans = async (courseId: string) => {
        const data = await safeFetch<Plan[]>(
            supabase.from('plans').select('*').eq('course_id', courseId),
            [],
            'Loading pricing plans'
        );
        if (data && data.length > 0) {
            // Fill defaults for any plan that has empty features
            const plansWithDefaults = data.map(plan => ({
                ...plan,
                features: (plan.features && plan.features.length > 0) ? plan.features : getDefaults(plan.name)
            }));
            setCoursePlans(plansWithDefaults);
        } else {
            // Reset to defaults if no plans found
            setCoursePlans([
                { name: 'STARTER', price: 4999, original_price: 9999, duration: '1 MONTH ACCESS', features: PLAN_DEFAULTS.STARTER, popular: false, plan_key: 'starter' },
                { name: 'PROFESSIONAL', price: 7999, original_price: 15999, duration: '3 MONTHS ACCESS', features: PLAN_DEFAULTS.PROFESSIONAL, popular: true, plan_key: 'professional' },
                { name: 'PREMIUM', price: 9999, original_price: 29999, duration: '6 MONTHS ACCESS', features: PLAN_DEFAULTS.PREMIUM, popular: false, plan_key: 'premium' }
            ]);
        }
    };

    const [modules, setModules] = useState<Module[]>([]);
    const [currentModule, setCurrentModule] = useState<Partial<Module>>({});
    const [isEditingModule, setIsEditingModule] = useState(false);

    const loadModules = async (courseId: string) => {
        const data = await safeFetch<Module[]>(
            supabase.from('course_modules').select('*').eq('course_id', courseId).order('order_index', { ascending: true }),
            [],
            'Loading modules'
        );
        setModules(data);
    };

    const fetchLessonsForBuilder = async (courseId: string) => {
        const data = await safeFetch<Lesson[]>(
            supabase.from('lessons').select('*').eq('course_id', courseId).order('order_index', { ascending: true }),
            [],
            'Loading lessons'
        );
        setLessons(data);
    };

    useEffect(() => {
        loadCourses();
    }, []);

    const handleFileUpload = async (file: File, bucket: string) => {
        if (!currentCourse.id) {
            toast.error('Save course basic info first');
            return null;
        }
        setIsUploading(true);
        try {
            const type = bucket.includes('video') ? 'videos' : bucket.includes('document') ? 'documents' : 'thumbnails';
            const path = storageService.getFilePath(currentCourse.id, currentLesson.id || 'course', type as any, file.name);
            const url = await storageService.uploadFile(file, bucket, path);
            return url;
        } catch (error: any) {
            if (error.message === 'FILE_TOO_LARGE_FRONTEND') {
                toast.error("Video exceeds maximum allowed size (1000MB)");
            } else if (error.message === 'FILE_TOO_LARGE_BACKEND') {
                toast.error("DATABASE ACTION REQUIRED: Run INCREASE_STORAGE_LIMITS.sql in Supabase SQL Editor to allow large uploads.", { duration: 10000 });
            } else {
                toast.error(`Upload failed: ${error.message}`);
            }
            return null;
        } finally {
            setIsUploading(false);
        }
    };

    const saveCourse = async () => {
        if (!currentCourse.title) {
            toast.error('Please enter a course title first');
            return null;
        }
        try {
            const courseData = {
                title: currentCourse.title,
                slug: currentCourse.slug || currentCourse.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
                description: currentCourse.description,
                thumbnail: currentCourse.thumbnail,
                status: currentCourse.status || 'draft'
            };

            let courseId = currentCourse.id;

            if (currentCourse.id) {
                const { error } = await supabase.from('courses').update(courseData).eq('id', currentCourse.id);
                if (error) throw error;
            } else {
                const { data, error } = await supabase.from('courses').insert([courseData]).select().single();
                if (error) throw error;
                courseId = data.id;
                setCurrentCourse(prev => ({ ...prev, id: courseId }));
            }

            // Sync Plans
            if (courseId) {
                for (const plan of coursePlans) {
                    const planData = {
                        ...plan,
                        course_id: courseId,
                        features: Array.isArray(plan.features) ? plan.features.filter(f => f.trim() !== '') : []
                    };

                    if (plan.id) {
                        await supabase.from('plans').update(planData).eq('id', plan.id);
                    } else {
                        const { data: newPlan } = await supabase.from('plans').insert([planData]).select().single();
                        if (newPlan) {
                            // Update local plans state with IDs
                            setCoursePlans(prev => prev.map(p => p.plan_key === plan.plan_key ? { ...p, id: newPlan.id } : p));
                        }
                    }
                }
            }

            toast.success('Course saved successfully');
            loadCourses();
            return courseId;
        } catch (error: any) {
            toast.error(`Error saving course: ${error.message}`);
            return null;
        }
    };

    const toggleStatus = async (id: string, currentStatus: string) => {
        const newStatus = currentStatus === 'active' ? 'draft' : 'active';
        try {
            const { error } = await supabase.from('courses').update({ status: newStatus }).eq('id', id);
            if (error) throw error;
            setCourses(courses.map(c => c.id === id ? { ...c, status: newStatus as any } : c));
            toast.info(`Course is now ${newStatus === 'active' ? 'Published' : 'Not Published'}`);
        } catch (error: any) {
            toast.error('Failed to update status');
        }
    };

    const deleteCourse = async (id: string) => {
        if (!confirm('Are you sure you want to delete this course? All lessons will be removed.')) return;
        try {
            const { error } = await supabase.from('courses').delete().eq('id', id);
            if (error) throw error;
            setCourses(courses.filter(c => c.id !== id));
            toast.success('Course deleted');
        } catch (error: any) {
            toast.error('Failed to delete course');
        }
    };

    // Lesson Management
    const [selectedCourse, setSelectedCourse] = useState<AdminCourse | null>(null);
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [isManagingLessons, setIsManagingLessons] = useState(false);
    const [isEditingLesson, setIsEditingLesson] = useState(false);
    const [currentLesson, setCurrentLesson] = useState<Partial<Lesson>>({});

    // Assets Management
    const [isManagingAssets, setIsManagingAssets] = useState(false);
    const [currentLessonAssets, setCurrentLessonAssets] = useState<LessonAsset[]>([]);
    const [isAddingAsset, setIsAddingAsset] = useState(false);
    const [newAsset, setNewAsset] = useState<Partial<LessonAsset>>({ type: 'pdf' });

    const openLessonManager = async (course: AdminCourse) => {
        setSelectedCourse(course);
        setCurrentCourse(course); // Keep in sync for builder logic
        setIsManagingLessons(true);
        fetchLessonsForBuilder(course.id);
    };

    const handleSaveLesson = async () => {
        const courseId = currentCourse.id || selectedCourse?.id;
        if (!courseId || !currentLesson.title) {
            toast.error('Course reference or title missing');
            return;
        }

        try {
            const lessonData: any = {
                course_id: courseId,
                module_id: currentLesson.module_id || null, // Ensure empty strings become NULL
                title: currentLesson.title.trim(),
                video_url: currentLesson.video_url || '',
                stream_video_id: currentLesson.stream_video_id || '',
                description: currentLesson.description || '',
                duration: currentLesson.duration || '',
                order_index: Number(currentLesson.order_index) || (lessons.length + 1)
            };

            console.log('--- SAVING LESSON ---', lessonData);

            if (currentLesson.id) {
                const { error } = await supabase.from('lessons').update(lessonData).eq('id', currentLesson.id);
                if (error) {
                    console.error('Update Error:', error);
                    throw error;
                }
                toast.success('Lesson updated');
            } else {
                const { error } = await supabase.from('lessons').insert([lessonData]);
                if (error) {
                    console.error('Insert Error:', error);
                    throw error;
                }
                toast.success('New lesson added');
            }
            setIsEditingLesson(false);
            fetchLessonsForBuilder(courseId);
        } catch (error: any) {
            console.error('LMS Save Error Detailed:', error);
            toast.error(`Error: ${error.message || 'Check terminal logs'}`);
        }
    };

    const openAssetManager = async (lesson: Lesson) => {
        setCurrentLesson(lesson);
        setIsManagingAssets(true);
        const data = await safeFetch<LessonAsset[]>(
            supabase.from('lesson_assets').select('*').eq('lesson_id', lesson.id),
            [],
            'Loading lesson files'
        );
        setCurrentLessonAssets(data);
    };

    const handleAddAsset = async () => {
        if (!currentLesson.id || !newAsset.title || (!newAsset.file_url && newAsset.type === 'link')) return;
        try {
            const { error } = await supabase.from('lesson_assets').insert([{
                lesson_id: currentLesson.id,
                title: newAsset.title,
                file_url: newAsset.file_url,
                type: newAsset.type
            }]);
            if (error) throw error;
            toast.success('File added to lesson');
            setIsAddingAsset(false);
            openAssetManager(currentLesson as Lesson);
        } catch (error) {
            toast.error('Failed to add file');
        }
    };

    const deleteAsset = async (assetId: string) => {
        try {
            const { error } = await supabase.from('lesson_assets').delete().eq('id', assetId);
            if (error) throw error;
            setCurrentLessonAssets(currentLessonAssets.filter(a => a.id !== assetId));
            toast.success('File removed');
        } catch (error) {
            toast.error('Failed to remove file');
        }
    };

    return (
        <AdminLayout>
            <div className="space-y-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Course List</h2>
                        <p className="text-slate-500 font-medium text-sm">Manage your course content and lessons.</p>
                    </div>
                    <Button
                        onClick={() => {
                            setCurrentCourse({ status: 'draft' });
                            setLessons([]);
                            setCoursePlans([
                                { name: 'STARTER', price: 4999, original_price: 9999, duration: '1 MONTH ACCESS', features: [], popular: false, plan_key: 'starter' },
                                { name: 'PROFESSIONAL', price: 7999, original_price: 15999, duration: '3 MONTHS ACCESS', features: [], popular: true, plan_key: 'professional' },
                                { name: 'PREMIUM', price: 9999, original_price: 29999, duration: '6 MONTHS ACCESS', features: [], popular: false, plan_key: 'premium' }
                            ]);
                            setIsEditingCourse(true);
                        }}
                        className="btn-navy h-14 rounded-2xl px-8 text-[10px] font-black uppercase tracking-widest shadow-premium"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add New Course
                    </Button>
                </div>

                <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Course Name</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Status</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Created Date</th>
                                    <th className="px-8 py-4 text-right text-[9px] font-black uppercase tracking-widest text-slate-400">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {isLoading ? (
                                    <tr><td colSpan={4} className="px-8 py-10 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">Loading courses...</td></tr>
                                ) : courses.map((course) => (
                                    <tr key={course.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 h-12 rounded-xl bg-slate-100 overflow-hidden shrink-0 border border-slate-200">
                                                    <img src={course.thumbnail} className="w-full h-full object-cover" alt="" />
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-[11px] font-black text-slate-900 uppercase tracking-tight truncate">{course.title}</span>
                                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate max-w-[200px]">{course.description}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${course.status === 'active'
                                                ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                                : 'bg-amber-50 text-amber-600 border-amber-100'
                                                }`}>
                                                {course.status === 'active' ? 'Published' : 'Not Published'}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                            {new Date(course.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setCurrentCourse(course);
                                                        loadPlans(course.id);
                                                        loadModules(course.id);
                                                        fetchLessonsForBuilder(course.id);
                                                        setIsEditingCourse(true);
                                                    }}
                                                    className="p-3 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-primary rounded-xl transition-all border border-slate-200"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => navigate(`/admin/courses/${course.id}`)} className="p-3 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-accent rounded-xl transition-all border border-slate-200">
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => toggleStatus(course.id, course.status)} className="p-3 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-primary rounded-xl transition-all border border-slate-200">
                                                    <Power className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => deleteCourse(course.id)} className="p-3 bg-slate-50 hover:bg-destructive/5 text-slate-400 hover:text-destructive rounded-xl transition-all border border-slate-200">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* COMPREHENSIVE COURSE BUILDER MODAL */}
            <Dialog open={isEditingCourse} onOpenChange={setIsEditingCourse}>
                <DialogContent className="rounded-[2.5rem] p-0 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col border-none shadow-2xl">
                    <div className="p-8 border-b border-slate-100 bg-white sticky top-0 z-20 flex justify-between items-center">
                        <div>
                            <DialogTitle className="text-2xl font-black uppercase tracking-tighter">
                                {currentCourse.id ? 'Edit Course' : 'Add New Course'}
                            </DialogTitle>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Create your course details</p>
                        </div>
                        <Button onClick={saveCourse} className="btn-gold h-12 rounded-xl px-8 text-[10px] font-black uppercase tracking-widest shadow-premium">
                            <Save className="w-4 h-4 mr-2" />
                            Save Course
                        </Button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-10 custom-scrollbar bg-slate-50/30">
                        <Tabs defaultValue="details" className="w-full">
                            <TabsList className="bg-slate-100 p-1.5 rounded-2xl mb-10 w-fit">
                                <TabsTrigger value="details" className="rounded-xl px-8 py-2.5 text-[10px] font-black uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm">1. Course Info</TabsTrigger>
                                <TabsTrigger value="plans" className="rounded-xl px-8 py-2.5 text-[10px] font-black uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm">2. Course Pricing</TabsTrigger>
                                <TabsTrigger value="lessons" className="rounded-xl px-8 py-2.5 text-[10px] font-black uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm">3. Lessons & Files</TabsTrigger>
                            </TabsList>

                            {/* TAB 1: CORE DETAILS */}
                            <TabsContent value="details" className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500 mt-0">
                                <div className="grid md:grid-cols-2 gap-10">
                                    <div className="space-y-6 bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Course Title</label>
                                            <Input value={currentCourse.title || ''} onChange={e => {
                                                const title = e.target.value;
                                                const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                                                setCurrentCourse({ ...currentCourse, title, slug: currentCourse.id ? currentCourse.slug : slug });
                                            }} placeholder="Professional Analysis Masterclass" className="h-14 rounded-2xl" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Terminal Slug (URL)</label>
                                            <Input value={currentCourse.slug || ''} onChange={e => setCurrentCourse({ ...currentCourse, slug: e.target.value })} placeholder="professional-analysis" className="h-12 rounded-xl" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Course Description</label>
                                            <Textarea value={currentCourse.description || ''} onChange={e => setCurrentCourse({ ...currentCourse, description: e.target.value })} placeholder="Deep dive into SMC and price action..." className="min-h-[150px] rounded-2xl p-4" />
                                        </div>
                                    </div>
                                    <div className="space-y-6">
                                        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-4">Course Thumbnail</label>
                                            <div className="space-y-6">
                                                <div className="aspect-[16/9] bg-slate-50 rounded-2xl overflow-hidden border border-slate-100 relative group flex items-center justify-center">
                                                    {currentCourse.thumbnail ? (
                                                        <img src={currentCourse.thumbnail} className="w-full h-full object-cover" alt="" />
                                                    ) : (
                                                        <div className="flex flex-col items-center opacity-20">
                                                            <Upload className="w-10 h-10 mb-2" />
                                                            <span className="text-[10px] font-black uppercase">No Image</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <input type="file" accept="image/*" onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        let courseId = currentCourse.id;
                                                        if (!courseId) {
                                                            const savedId = await saveCourse();
                                                            if (!savedId) return;
                                                            courseId = savedId;
                                                        }

                                                        setIsUploading(true);
                                                        try {
                                                            const path = storageService.getFilePath(courseId, 'course', 'thumbnails', file.name);
                                                            const url = await storageService.uploadFile(file, 'course-thumbnails', path);
                                                            if (url) {
                                                                setCurrentCourse(prev => ({ ...prev, thumbnail: url }));
                                                                // Immediately partial update title/desc to avoid losing them if not saved
                                                                await supabase.from('courses').update({ thumbnail: url }).eq('id', courseId);
                                                                toast.success('Thumbnail uploaded and linked!');
                                                            }
                                                        } catch (err: any) {
                                                            toast.error(`Upload failed: ${err.message}`);
                                                        } finally {
                                                            setIsUploading(false);
                                                        }
                                                    }
                                                }} className="hidden" id="builder-thumb-upload" />
                                                <label htmlFor="builder-thumb-upload" className="flex items-center justify-center gap-2 h-14 bg-slate-50 border border-dashed border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-100 transition-all text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                    <Upload className="w-4 h-4" />
                                                    {isUploading ? 'Uploading...' : 'Upload Image'}
                                                </label>
                                            </div>
                                        </div>
                                        <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex items-center justify-between">
                                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Course Status</span>
                                            <div className="flex gap-2">
                                                <button onClick={() => setCurrentCourse({ ...currentCourse, status: 'active' })} className={cn("px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all", currentCourse.status === 'active' ? "bg-emerald-500 text-white shadow-lg shadow-emerald-200" : "bg-slate-100 text-slate-400")}>Published</button>
                                                <button onClick={() => setCurrentCourse({ ...currentCourse, status: 'draft' })} className={cn("px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all", currentCourse.status === 'draft' ? "bg-amber-500 text-white shadow-lg shadow-amber-200" : "bg-slate-100 text-slate-400")}>Not Published</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* TAB 2: PLANS */}
                            <TabsContent value="plans" className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500 mt-0">
                                <div className="grid grid-cols-3 gap-6">
                                    {coursePlans.map((plan, idx) => (
                                        <div key={idx} className={cn(
                                            "bg-white p-8 rounded-[2rem] border-2 transition-all shadow-sm flex flex-col gap-6",
                                            plan.popular ? "border-accent shadow-xl shadow-accent/5 ring-8 ring-accent/5" : "border-slate-100"
                                        )}>
                                            <div>
                                                <h4 className="text-xl font-black text-slate-900 uppercase tracking-tighter mb-1">{plan.name}</h4>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{plan.plan_key} Plan</p>
                                            </div>

                                            <div className="space-y-4">
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Price (₹)</label>
                                                    <Input type="number" value={plan.price} onChange={e => {
                                                        const newPlans = [...coursePlans];
                                                        newPlans[idx].price = parseInt(e.target.value);
                                                        setCoursePlans(newPlans);
                                                    }} className="h-12 rounded-xl" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Original (Strikethrough)</label>
                                                    <Input type="number" value={plan.original_price || 0} onChange={e => {
                                                        const newPlans = [...coursePlans];
                                                        newPlans[idx].original_price = parseInt(e.target.value);
                                                        setCoursePlans(newPlans);
                                                    }} className="h-12 rounded-xl" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Access Duration</label>
                                                    <Input value={plan.duration} onChange={e => {
                                                        const newPlans = [...coursePlans];
                                                        newPlans[idx].duration = e.target.value;
                                                        setCoursePlans(newPlans);
                                                    }} className="h-12 rounded-xl" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Features (Comma Separated)</label>
                                                    <Textarea value={(plan.features || []).join(', ')} onChange={e => {
                                                        const newPlans = [...coursePlans];
                                                        newPlans[idx].features = e.target.value.split(',').map(f => f.trim());
                                                        setCoursePlans(newPlans);
                                                    }} className="h-24 rounded-xl text-[10px]" />
                                                </div>
                                            </div>

                                            <div className="mt-auto pt-4 flex items-center gap-3">
                                                <button onClick={() => {
                                                    const newPlans = coursePlans.map((p, i) => ({ ...p, popular: i === idx }));
                                                    setCoursePlans(newPlans);
                                                }} className={cn("flex-1 h-10 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all", plan.popular ? "bg-accent text-white" : "bg-slate-50 text-slate-400")}>
                                                    {plan.popular ? 'Recommended ✓' : 'Set Recommended'}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </TabsContent>

                            {/* TAB 3: LESSONS & ASSETS (CURRICULUM) */}
                            <TabsContent value="lessons" className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500 mt-0">
                                {!currentCourse.id ? (
                                    <div className="p-20 bg-white border-2 border-dashed border-slate-100 rounded-[3rem] text-center">
                                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                            <Play className="w-8 h-8 text-slate-200" />
                                        </div>
                                        <h4 className="text-xl font-black text-slate-900 uppercase tracking-tighter mb-4">Save Course First</h4>
                                        <p className="text-slate-400 text-sm max-w-sm mx-auto font-medium">Please save the <b>Course Info</b> first to start adding modules and lessons.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-10">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tighter">Course Curriculum</h3>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Organize your content into modules and lessons</p>
                                            </div>
                                            <Button onClick={() => {
                                                setCurrentModule({ order_index: modules.length + 1 });
                                                setIsEditingModule(true);
                                            }} className="h-10 rounded-xl bg-primary text-white text-[9px] font-black uppercase tracking-widest">
                                                <Plus className="w-3.5 h-3.5 mr-1" /> Add Module
                                            </Button>
                                        </div>

                                        {/* MODULES & LESSONS LIST */}
                                        <div className="space-y-8">
                                            {modules.map((module) => (
                                                <div key={module.id} className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
                                                    <div className="bg-slate-50 px-8 py-4 flex items-center justify-between border-b border-slate-100">
                                                        <div className="flex items-center gap-4">
                                                            <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center font-black text-[10px] text-primary">{module.order_index}</div>
                                                            <h4 className="text-sm font-black text-slate-900 uppercase tracking-tight">{module.title}</h4>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Button onClick={() => {
                                                                setCurrentLesson({ module_id: module.id, order_index: lessons.filter(l => l.module_id === module.id).length + 1 });
                                                                setIsEditingLesson(true);
                                                            }} variant="ghost" className="h-9 px-4 text-[9px] font-black uppercase tracking-widest text-primary hover:bg-white">
                                                                <Plus className="w-3.5 h-3.5 mr-1" /> Add Lesson
                                                            </Button>
                                                            <button onClick={() => {
                                                                setCurrentModule(module);
                                                                setIsEditingModule(true);
                                                            }} className="p-2 text-slate-400 hover:text-primary transition-colors"><Edit2 className="w-4 h-4" /></button>
                                                            <button onClick={async () => {
                                                                if (confirm('Delete module and its lessons?')) {
                                                                    await supabase.from('course_modules').delete().eq('id', module.id);
                                                                    loadModules(currentCourse.id!);
                                                                }
                                                            }} className="p-2 text-slate-400 hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                                                        </div>
                                                    </div>
                                                    <div className="p-4 space-y-2">
                                                        {lessons.filter(l => l.module_id === module.id).map((lesson) => (
                                                            <div key={lesson.id} className="flex items-center justify-between p-4 bg-white hover:bg-slate-50 border border-slate-50 rounded-xl transition-all group">
                                                                <div className="flex items-center gap-4">
                                                                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[9px] font-black text-slate-400">{lesson.order_index}</div>
                                                                    <div>
                                                                        <p className="text-[11px] font-black text-slate-900 uppercase tracking-tight">{lesson.title}</p>
                                                                        <div className="flex items-center gap-3 mt-1">
                                                                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{lesson.duration || '00:00'} Duration</span>
                                                                            {lesson.video_url && (
                                                                                <span className="flex items-center gap-1 text-[8px] font-black text-emerald-600 uppercase">
                                                                                    <Video className="w-2.5 h-2.5" /> Video Ready
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <button onClick={() => openAssetManager(lesson)} className="p-2 text-slate-300 hover:text-primary transition-colors"><FileText className="w-4 h-4" /></button>
                                                                    <button onClick={() => {
                                                                        setCurrentLesson(lesson);
                                                                        setIsEditingLesson(true);
                                                                    }} className="p-2 text-slate-300 hover:text-primary transition-colors"><Edit2 className="w-4 h-4" /></button>
                                                                    <button onClick={async () => {
                                                                        if (confirm('Delete lesson?')) {
                                                                            await supabase.from('lessons').delete().eq('id', lesson.id);
                                                                            fetchLessonsForBuilder(currentCourse.id!);
                                                                        }
                                                                    }} className="p-2 text-slate-300 hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {lessons.filter(l => l.module_id === module.id).length === 0 && (
                                                            <p className="text-center py-6 text-[9px] font-black uppercase text-slate-300 tracking-[0.2em] italic">No lessons in this module</p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}

                                            {modules.length === 0 && (
                                                <div className="p-20 bg-white border border-dashed border-slate-200 rounded-[3rem] text-center">
                                                    <p className="text-slate-400 text-sm font-medium">Capture your structure by adding your first module.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </TabsContent>
                        </Tabs>
                    </div>
                </DialogContent>
            </Dialog>

            {/* MODULE EDITOR MODAL */}
            <Dialog open={isEditingModule} onOpenChange={setIsEditingModule}>
                <DialogContent className="max-w-md rounded-[2.5rem] p-10 bg-white border-none shadow-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-black uppercase tracking-tighter">{currentModule.id ? 'Edit Module' : 'Add New Module'}</DialogTitle>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Group your lessons within this module</p>
                    </DialogHeader>
                    <div className="space-y-6 mt-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Module Title</label>
                            <Input value={currentModule.title || ''} onChange={e => setCurrentModule({ ...currentModule, title: e.target.value })} placeholder="Foundations" className="h-14 rounded-2xl" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Order Index</label>
                            <Input type="number" value={currentModule.order_index || 0} onChange={e => setCurrentModule({ ...currentModule, order_index: parseInt(e.target.value) })} className="h-14 rounded-2xl" />
                        </div>
                        <Button onClick={async () => {
                            if (!currentModule.title || !currentCourse.id) return;
                            const moduleData = { course_id: currentCourse.id, title: currentModule.title, order_index: currentModule.order_index || 0 };
                            if (currentModule.id) {
                                await supabase.from('course_modules').update(moduleData).eq('id', currentModule.id);
                            } else {
                                await supabase.from('course_modules').insert([moduleData]);
                            }
                            toast.success('Module saved');
                            setIsEditingModule(false);
                            loadModules(currentCourse.id);
                        }} className="btn-gold w-full h-14 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-premium">Save Module</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* LESSON EDITOR MODAL (UPGRADED WITH MP4 UPLOAD) */}
            <Dialog open={isEditingLesson} onOpenChange={setIsEditingLesson}>
                <DialogContent className="max-w-3xl rounded-[2.5rem] p-10 bg-white border-none shadow-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-black uppercase tracking-tighter">{currentLesson.id ? 'Edit Lesson' : 'Add New Lesson'}</DialogTitle>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Configure your content and video node</p>
                    </DialogHeader>
                    <div className="space-y-8 mt-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Lesson Title</label>
                                <Input value={currentLesson.title || ''} onChange={e => setCurrentLesson({ ...currentLesson, title: e.target.value })} placeholder="Market Introduction" className="h-14 rounded-2xl" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Duration</label>
                                <Input value={currentLesson.duration || ''} onChange={e => setCurrentLesson({ ...currentLesson, duration: e.target.value })} placeholder="15:00" className="h-14 rounded-2xl" />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Cloudflare Stream ID (Secure)</label>
                            <Input
                                value={currentLesson.stream_video_id || ''}
                                onChange={e => setCurrentLesson({ ...currentLesson, stream_video_id: e.target.value })}
                                placeholder="Paste Cloudflare Video ID here"
                                className="h-14 rounded-2xl border-primary/20 bg-primary/5"
                            />
                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">Example: 5d53507119114f774e308f7f2b69... (Do not use YouTube URLs here)</p>
                        </div>

                        <div className="space-y-4">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Legacy Video Node (YouTube/MP4 - Non-Secure)</label>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Input value={currentLesson.video_url || ''} onChange={e => setCurrentLesson({ ...currentLesson, video_url: e.target.value })} placeholder="YouTube or Storage URL" className="h-14 rounded-2xl" />
                                </div>
                                <div className="relative">
                                    <input type="file" accept="video/mp4" onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file && currentCourse.id) {
                                            const url = await handleFileUpload(file, 'course-videos');
                                            if (url) {
                                                setCurrentLesson({ ...currentLesson, video_url: url });

                                                // Automatic Persistence to DB if lesson exists
                                                if (currentLesson.id) {
                                                    const { error: syncError } = await supabase
                                                        .from('lessons')
                                                        .update({ video_url: url })
                                                        .eq('id', currentLesson.id);

                                                    if (!syncError) {
                                                        toast.success('Database Synchronized');
                                                        fetchLessonsForBuilder(currentCourse.id);
                                                    }
                                                }
                                                toast.success('Video uploaded successfully');
                                            }
                                        }
                                    }} className="hidden" id="lesson-video-upload" />
                                    <label htmlFor="lesson-video-upload" className="flex items-center justify-center gap-2 h-14 bg-slate-50 border border-dashed border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-100 transition-all text-[10px] font-black uppercase tracking-widest text-slate-400 shadow-sm">
                                        <Video className="w-4 h-4" />
                                        {isUploading ? 'Uploading...' : 'Upload MP4'}
                                    </label>
                                </div>
                            </div>
                            {currentLesson.video_url?.includes('supabase') && <p className="text-[8px] font-black text-emerald-600 uppercase tracking-widest text-center mt-2">✓ Internal Storage Link Active</p>}
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Lesson Description (Intel)</label>
                            <Textarea value={currentLesson.description || ''} onChange={e => setCurrentLesson({ ...currentLesson, description: e.target.value })} placeholder="Context for the student..." className="min-h-[100px] rounded-2xl p-4" />
                        </div>

                        <div className="flex gap-4">
                            <Button onClick={handleSaveLesson} className="btn-gold h-14 flex-1 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-premium">Save Lesson</Button>
                            <Button variant="outline" onClick={() => setIsEditingLesson(false)} className="h-14 flex-1 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-400">Cancel</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ASSET MANAGER OVERLAY */}
            <Dialog open={isManagingAssets} onOpenChange={setIsManagingAssets}>
                <DialogContent className="max-w-2xl rounded-[3rem] p-10 bg-white border-none shadow-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-black uppercase tracking-tighter">Lesson Files & Links</DialogTitle>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Lesson: {currentLesson?.title}</p>
                    </DialogHeader>

                    <div className="space-y-8 mt-6">
                        <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 space-y-6">
                            <div className="flex gap-2 p-1.5 bg-white rounded-2xl w-fit">
                                <button onClick={() => setNewAsset({ ...newAsset, type: 'pdf' })} className={cn("px-6 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all", newAsset.type === 'pdf' ? "bg-primary text-white shadow-md" : "text-slate-400")}>PDF Document</button>
                                <button onClick={() => setNewAsset({ ...newAsset, type: 'link' })} className={cn("px-6 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all", newAsset.type === 'link' ? "bg-primary text-white shadow-md" : "text-slate-400")}>External Link</button>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">File or Link Title</label>
                                    <Input value={newAsset.title || ''} onChange={e => setNewAsset({ ...newAsset, title: e.target.value })} placeholder="Lesson PDF Guide" className="h-14 rounded-2xl border-white focus:border-accent shadow-sm" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                                        {newAsset.type === 'pdf' ? 'Upload PDF' : 'Paste Website URL'}
                                    </label>
                                    {newAsset.type === 'pdf' ? (
                                        <div className="relative">
                                            <input type="file" accept=".pdf" onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const url = await handleFileUpload(file, 'course-documents');
                                                    if (url) setNewAsset({ ...newAsset, file_url: url });
                                                }
                                            }} className="hidden" id="asset-upload-builder" />
                                            <label htmlFor="asset-upload-builder" className="flex items-center justify-center gap-2 h-14 bg-white border border-dashed border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest text-slate-400 shadow-sm">
                                                <Upload className="w-4 h-4" />
                                                {isUploading ? 'Uploading...' : 'Click to Upload PDF'}
                                            </label>
                                            {newAsset.file_url && <p className="text-[8px] font-black text-emerald-600 mt-2 uppercase text-center truncate">File Uploaded Successfully ✓</p>}
                                        </div>
                                    ) : (
                                        <Input value={newAsset.file_url || ''} onChange={e => setNewAsset({ ...newAsset, file_url: e.target.value })} placeholder="https://..." className="h-14 rounded-2xl border-white focus:border-accent shadow-sm" />
                                    )}
                                </div>
                            </div>
                            <Button onClick={handleAddAsset} className="btn-gold w-full h-14 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-premium">Add to Lesson</Button>
                        </div>

                        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                            {currentLessonAssets.map((asset) => (
                                <div key={asset.id} className="flex items-center justify-between p-5 bg-white rounded-2xl border border-slate-100 group hover:border-slate-300 transition-all">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-accent group-hover:text-white transition-all shadow-inner">
                                            {asset.type === 'pdf' ? <FileText className="w-5 h-5" /> : <LinkIcon className="w-5 h-5" />}
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black uppercase tracking-tight text-slate-900">{asset.title}</p>
                                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest truncate max-w-[150px]">{asset.file_url}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => deleteAsset(asset.id)} className="p-2 text-slate-200 hover:text-destructive transition-colors"><X className="w-4 h-4" /></button>
                                </div>
                            ))}
                            {currentLessonAssets.length === 0 && <p className="text-center py-10 text-[10px] font-black uppercase text-slate-200 tracking-[0.2em] italic">No files added yet</p>}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default AdminCourses;
